// src/FlightBreakdown.js
import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import './FlightBreakdown.css';
import PasteData from './PasteData';
import Breakdown from './Breakdown';

// Threshold preferences persist across sessions because they're a dispatcher
// preference; the priority/freight-manifest toggles do NOT because they're
// per-flight context, not preferences.
const PREFS_KEY = 'dispatcherBreakdownPrefs';
const DEFAULT_HOT_THRESHOLD = 30;
const DEFAULT_COLD_THRESHOLD_HOURS = 2;

const loadStoredPrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

function FlightBreakdownPage() {
  const [flights, setFlights] = useState([]);
  const [isPriorityFlight, setIsPriorityFlight] = useState(false);
  const [hasFreightManifest, setHasFreightManifest] = useState(false);
  const [hotThreshold, setHotThreshold] = useState(
    () => loadStoredPrefs()?.hotThreshold ?? DEFAULT_HOT_THRESHOLD
  );
  const [coldThresholdHours, setColdThresholdHours] = useState(
    () => loadStoredPrefs()?.coldThresholdHours ?? DEFAULT_COLD_THRESHOLD_HOURS
  );

  // Note for all drivers - load from localStorage
  const [flightNote, setFlightNote] = useState(() => {
    return localStorage.getItem('flightNoteForAllDrivers') || '';
  });

  // Save flight note to localStorage when it changes. A throw here (storage
  // full, private mode) must not crash the page — the note stays in state.
  useEffect(() => {
    try { localStorage.setItem('flightNoteForAllDrivers', flightNote); } catch {}
  }, [flightNote]);

  // Persist threshold preferences whenever they change.
  useEffect(() => {
    try {
      localStorage.setItem(
        PREFS_KEY,
        JSON.stringify({ hotThreshold, coldThresholdHours })
      );
    } catch {
      // localStorage can throw in private-browsing or when full; preferences
      // simply won't persist this session.
    }
  }, [hotThreshold, coldThresholdHours]);

  const handleParametersChange = (params) => {
    setIsPriorityFlight(params.isPriorityFlight);
    setHasFreightManifest(params.hasFreightManifest);
    setHotThreshold(params.hotThreshold);
    setColdThresholdHours(params.coldThresholdHours);
  };

  // The main div now directly contains the page-specific content
  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', color: '#fff' }}> {/* Adjust minHeight if needed */}
      {/* NavBar and Offset Box are REMOVED from here */}

      <div className="breakdown-container">
        <div className="breakdown-card">
          <p>Paste your TDR and Brock / SmartSuite data below, then run the breakdown.</p>
          
          <div className="breakdown-content">
            <div className="parameters-section">
              <h3>Threshold Parameters</h3>
              <Breakdown
                flights={flights}
                onParametersChange={handleParametersChange}
                isPriorityFlight={isPriorityFlight}
                hasFreightManifest={hasFreightManifest}
                hotThreshold={hotThreshold}
                coldThresholdHours={coldThresholdHours}
              />

              {/* Flight Note for All Drivers */}
              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ color: '#F9B612', marginBottom: '12px' }}>Note for All Drivers</h3>
                <TextField
                  value={flightNote}
                  onChange={(e) => setFlightNote(e.target.value)}
                  placeholder="Enter a note that will appear on every driver's printout..."
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#F9B612' },
                    },
                    '& .MuiInputBase-input': { color: '#e2e2e2' },
                    '& .MuiInputLabel-root': { color: '#e2e2e2' },
                  }}
                />
              </div>
            </div>
            
            <div className="paste-section">
              <h3>Paste TDR and Brock / SmartSuite Data</h3>
              <PasteData
                setFlights={setFlights}
                breakdownParams={{
                  isPriorityFlight,
                  hasFreightManifest,
                  hotThreshold,
                  coldThresholdHours,
                  flightNote
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlightBreakdownPage;
